import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { debounce } from 'throttle-debounce'
import clsx from 'clsx'
import { TextInput } from '@astrid/components'
import useEnhancedState from 'hooks/useEnhancedState'
import useThunkDispatch from 'hooks/useThunkDispatch'
import { ReactComponent as SearchIcon } from 'resources/graphics/icons/search.svg'
import { ReactComponent as CloseIcon } from 'resources/graphics/icons/close.svg'
import { ReactComponent as PlusIcon } from 'resources/graphics/icons/plus.svg'
import { addWordToStudySet, deleteWordFromStudySet, selectCurrentStudySet } from 'store/services/Exercise'

import api from '../../api/api'
import AnimatedCheckmark from './AnimatedCheckmark'
import styles from './SearchWords.module.scss'

export const SearchWords = () => {
  const [state, setState] = useEnhancedState<{ searchVal: string; foundWords: string[]; loading: boolean }>({
    searchVal: '',
    foundWords: [],
    loading: false
  })
  const currentStudySet = useSelector(selectCurrentStudySet)
  const dispatch = useThunkDispatch()

  const wordsLeftCount = currentStudySet ? 10 - currentStudySet.words.length : null

  const searchWords = useMemo(
    () =>
      debounce(
        500,
        async (val: string) => {
          if (val.length < 2) {
            setState({ foundWords: [], loading: false })
            return
          }
          setState({ loading: true })
          try {
            const { data } = await api.get<string[]>('/study-set/words', {
              params: {
                $search: val
              }
            })

            setState({ foundWords: data, loading: false })
          } catch (error) {
            setState({ loading: false })
          }
        },
        { atBegin: false }
      ),
    []
  )

  const onPlusPress = (wordToAddOrRemove: string) => {
    if (currentStudySet?.words.some(({ word }) => word === wordToAddOrRemove)) {
      dispatch(deleteWordFromStudySet(currentStudySet._id, wordToAddOrRemove))
    } else if (currentStudySet && currentStudySet.words.length < 10) {
      dispatch(addWordToStudySet(currentStudySet._id, wordToAddOrRemove))
    }
  }

  useEffect(() => {
    searchWords(state.searchVal)
  }, [state.searchVal])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.title}>Add words</span>
        {typeof wordsLeftCount === 'number' ? (
          <span className={clsx(styles.wordsLeft, wordsLeftCount === 0 && styles.noWordsLeft)}>
            {wordsLeftCount} word{wordsLeftCount === 1 ? '' : 's'} left
          </span>
        ) : null}
      </div>
      <div className={styles.inputContainer}>
        <TextInput
          id="search"
          name="search"
          value={state.searchVal}
          onChange={(e) => setState({ searchVal: e.currentTarget.value })}
        />
        {!!state.searchVal ? (
          <button
            type="button"
            className={styles.clearInputButton}
            aria-label={'Clear search value'}
            onClick={() => setState({ searchVal: '' })}
          >
            <CloseIcon />
          </button>
        ) : (
          <SearchIcon className={styles.searchIcon} />
        )}
      </div>
      {state.foundWords.map((res, i) => (
        <button
          type="button"
          aria-label={`Add word ${res}`}
          className={styles.word}
          key={`result-${i}`}
          onClick={() => onPlusPress(res)}
        >
          <span className={styles.wordText}>{res}</span>
          {currentStudySet?.words.some(({ word }) => word === res) ? (
            <AnimatedCheckmark />
          ) : (
            <PlusIcon fill={'#78C7F5'} width={14} height={14} />
          )}
        </button>
      ))}
    </div>
  )
}
